import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout, Input, Select, Card, Button, Tag, message, Spin, Pagination } from 'antd';
import {
  SearchOutlined,
  GithubOutlined,
  RocketOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  ExperimentOutlined,
  WalletOutlined,
  ShoppingCartOutlined,
  FolderOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import { PublishedStrategy, PaginationMeta } from '../../types';
import apiService from '../../services/api';
import { ServiceType } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import StrategyMarketplace from './components/StrategyMarketplace';
import {
  HomeContainer,
  MainContent,
  PageHeader,
  PageTitle,
  PageSubtitle
} from './styles';

const { Content } = Layout;
const { Option } = Select;
const coreService = apiService.getService(ServiceType.CORE);

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [strategies, setStrategies] = useState<PublishedStrategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/signin');
    }
  }, [isAuthenticated, navigate]);

  // Fetch strategies
  const fetchStrategies = useCallback(async (page: number = 1, limit: number = 12, search?: string, type?: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/signin');
        return;
      }

      const params: any = { page, limit };
      if (search) params.q = search;
      if (type && type !== 'all') params.type = type;

      const response = await coreService.get('/strategies/published', {
        params,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.data.data) {
        setStrategies(response.data.data);
        if (response.data.meta) {
          setPagination(response.data.meta);
        }
      } else if (Array.isArray(response.data)) {
        setStrategies(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching strategies:', error);
      message.error('Failed to load strategies');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStrategies(pagination.page, pagination.limit, searchQuery || undefined, filterType !== 'all' ? filterType : undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, pagination.page, pagination.limit, searchQuery, filterType]);

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (value: string) => {
    setFilterType(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    setPagination(prev => ({
      ...prev,
      page,
      limit: pageSize || prev.limit
    }));
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <HomeContainer>
      <Content style={{ padding: '24px' }}>
        <PageHeader>
          <div>
            <PageTitle>Strategy Marketplace</PageTitle>
            <PageSubtitle>
              Pick a strategy to set it up and run it
            </PageSubtitle>
          </div>
        </PageHeader>

        <StrategyMarketplace
          strategies={strategies}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          filterType={filterType}
          onSearch={handleSearch}
          onFilterChange={handleFilterChange}
          onPageChange={handlePageChange}
        />
      </Content>
    </HomeContainer>
  );
};

export default HomePage;

